/* @flow */
import * as React from "react";
import { TransitionGroup } from "react-transition-group";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import Refund from "../common/Refund";
import Money from "../Money";
import { tournamentCurrency } from "../Icons/Kuchka";
import { Fade } from "../common/Animations";
import { Sound } from "../Audio";

import { fontSize } from "../common/styled-mixins";

import type { News as NewsObject } from "../../redux/reducer/news";

const Container = styled.div`
  position: absolute;
  top: 24px;
  right: 3px;
  max-width: 500px;
  z-index: 1000;

  ${props =>
    props.mobile &&
    css`
      top: ${props.tour ? "30px" : "calc(86px + 15px)"};
      right: 0;
      left: 0;
      max-width: 100%;

      @media (max-width: 959px) and (min-width: 480px) {
        right: ${props.tour ? 0 : "146px"};
      }
    `};
`;

const Item = styled.div`
  position: relative;
  min-height: 54px;
  line-height: 54px;
  padding-right: 83px;
  text-align: right;
  background-color: rgba(231, 76, 60, 0.25);
  background: linear-gradient(
    to left,
    rgba(231, 76, 60, 0.5) 0%,
    rgba(231, 76, 60, 0) 100%
  );
  background-clip: padding-box;

  ${props =>
    props.positive &&
    css`
      background-color: rgba(96, 163, 28, 0.25);
      background: linear-gradient(
        to left,
        rgba(96, 163, 28, 0.5) 0%,
        rgba(96, 163, 28, 0) 100%
      );
    `} ${props =>
    props.mobile &&
    css`
      min-height: 27px;
      padding-right: 45px;
      line-height: 27px;
      background: rgba(231, 76, 60, 0.3);

      ${props =>
        props.positive &&
        css`
          background: rgba(96, 163, 28, 0.3);
        `};
    `};
`;

const Close = styled.div`
  position: absolute;
  top: 50%;
  right: 45px;
  transform: translateY(-50%);
  width: 19px;
  height: 19px;
  opacity: 0.38;
  transition: all 0.3s ease;
  cursor: pointer;

  :before,
  :after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    transform-origin: 50%;
    display: block;
  }

  :before {
    width: 100%;
    border-top: 2px solid #fff;
  }
  :after {
    height: 100%;
    border-left: 2px solid #fff;
  }

  :hover {
    opacity: 1;
  }

  ${props =>
    props.mobile &&
    css`
      opacity: .7;
      width: 15px;
      height: 15px;
      right: 15px;
    `};
`;

const Text = styled.div`
  display: inline-block;
  vertical-align: middle;
  font-weight: 600;
  font-size: ${fontSize(14)};
  text-transform: uppercase;
  text-align: left;
  color: rgba(231, 76, 60, .7);

  ${props =>
    props.positive &&
    css`
      color: rgba(96, 163, 28, .7);
    `} ${props =>
    props.mobile &&
    css`
      width: 50%;
      padding-right: 14px;
      font-size: ${fontSize(12)};
      text-align: right;
      color: rgba(255, 255, 255, .7);
    `};
`;

const ValueContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-left: 36px;
  font-weight: 400;
  font-size: ${fontSize(14)};
  color: rgba(255, 255, 255, .7);

  ${props =>
    props.mobile &&
    css`
      min-width: 124px;
      margin-left: 0;
      text-align: left;
      font-size: ${fontSize(12)};
    `};
`;

const Value = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: 14px;
  font-weight: 700;
  font-size: 22px;

  ${props =>
    props.mobile &&
    css`
      margin-left: 4px;
      font-size: ${fontSize(14)};
    `};
`;

type ItemProps = {
  tradeMode: "tournament" | "binary",
  payload: $PropertyType<NewsObject, "payload">,
  onDismiss: () => void,
  mobile?: boolean,
  style: { [key: string]: string | number },
  soundsEnabled?: boolean
};

class NewsItem extends React.Component<ItemProps> {
  timer: TimeoutID;

  componentDidMount() {
    this.timer = setTimeout(this.props.onDismiss, 2000);
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  render() {
    const {
      tradeMode,
      mobile,
      onDismiss,
      style,
      payload,
      soundsEnabled
    } = this.props;
    const positive = payload.profit >= 0;

    const currency = tradeMode === "tournament" ? tournamentCurrency : null;

    return (
      <Item
        positive={positive}
        mobile={mobile}
        onClick={onDismiss}
        style={style}
      >
        {soundsEnabled && (
          <Sound play={positive ? "PLAY_WIN_SOUND" : "PLAY_LOOSE_SOUND"} />
        )}

        <Close onClick={onDismiss} mobile={mobile} />

        <Text mobile={mobile} positive={positive}>
          <Gettext>Operation closed</Gettext>
        </Text>

        <ValueContainer mobile={mobile}>
          <Gettext>Income</Gettext>:
          <Value mobile={mobile}>
            {payload.profit === 0 ? (
              <Refund />
            ) : (
              <Money currency={currency}>{payload.profit}</Money>
            )}
          </Value>
        </ValueContainer>
      </Item>
    );
  }
}

type Props = {
  news: Array<NewsObject>,
  onDismiss: number => void,
  mobile?: boolean,
  soundsEnabled?: boolean,
  tour?: boolean
};

function News(props: Props) {
  return (
    <Container mobile={props.mobile} tour={props.tour}>
      <TransitionGroup component={null}>
        {props.news.filter(news => !news.isDismissed).map(news => (
          <Fade in={news.isDismissed} key={news.payload.id}>
            {style => (
              <NewsItem
                style={style}
                tradeMode={news.isTournament ? "tournament" : "binary"}
                soundsEnabled={props.soundsEnabled}
                payload={news.payload}
                mobile={props.mobile}
                onDismiss={() => props.onDismiss(news.payload.id)}
              />
            )}
          </Fade>
        ))}
      </TransitionGroup>
    </Container>
  );
}

export default News;



// WEBPACK FOOTER //
// ./src/components/News/News.js